/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  InputAdornment,
  type InputBaseComponentProps,
  type SxProps,
  TextField,
  type TextFieldProps,
  type TextFieldVariants,
  type Theme,
} from '@mui/material';
import { get } from 'lodash';
import { type ClipboardEventHandler, useMemo } from 'react';
import { TextFieldNumberFormatCustom } from '../pa-formik/TextFieldNumberFormatCustom';

type HtmlInputSlotProps = InputBaseComponentProps & { sx?: SxProps<Theme> };
type InputSlotProps = InputBaseComponentProps & {
  inputComponent?: React.ElementType<InputBaseComponentProps>;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
};

export type BaseTextFieldProps<Variant extends TextFieldVariants = TextFieldVariants> = Omit<
  TextFieldProps<Variant>,
  'size'
> & {
  numberFormat?:
    | boolean
    | 'dollar'
    | { dollar?: boolean; fixedDecimalScale?: boolean; decimalScale?: number };
  onPaste?: ClipboardEventHandler<HTMLDivElement>;
  rightUnit?: string;
  leftUnit?: string;
  textAlign?: 'left' | 'right';
  size?: TextFieldProps['size'] | 'tiny' | 'xs';
};

export function BaseTextField({
  numberFormat,
  rightUnit,
  leftUnit,
  textAlign,
  size,
  ...rest
}: Readonly<BaseTextFieldProps>) {
  const inputProps = useMemo(() => {
    if (typeof numberFormat === 'object') {
      return {
        'data-fixeddecimalscale': numberFormat.fixedDecimalScale,
        'data-decimalscale': numberFormat.decimalScale,
        'data-dollarvalue': `${numberFormat.dollar ?? false}`,
      };
    }

    if (numberFormat === 'dollar') {
      return {
        'data-fixeddecimalscale': false,
        'data-decimalscale': 2,
        'data-dollarvalue': 'true',
      };
    }

    return {};
  }, [numberFormat]);

  const inputAdornmentSx = useMemo(() => {
    if (size === 'tiny') {
      return { '& .MuiTypography-root': { fontSize: 12, lineHeight: 1 }, pr: 1 };
    }
    return { pr: 1 };
  }, [size]);

  const isNativeNumber = rest.type === 'number';
  const htmlInputSlotProps =
    typeof rest.slotProps?.htmlInput === 'function'
      ? {}
      : ((rest.slotProps?.htmlInput as HtmlInputSlotProps | undefined) ?? {});
  const inputSlotProps =
    typeof rest.slotProps?.input === 'function'
      ? {}
      : ((rest.slotProps?.input as InputSlotProps | undefined) ?? {});

  return (
    <TextField
      {...rest}
      className={[
        rest.className,
        size === 'tiny' || size === 'xs' ? `pa-textfield-size-${size}` : undefined,
      ]
        .filter(Boolean)
        .join(' ')}
      fullWidth
      size={size !== 'tiny' && size !== 'xs' ? (size ?? 'small') : 'small'}
      data-testid={get(rest, 'data-testid') ?? rest.name?.split('.').at(-1)}
      slotProps={{
        htmlInput: {
          ...inputProps,
          ...htmlInputSlotProps,
          sx: textAlign ? { textAlign } : htmlInputSlotProps.sx,
        },
        input: {
          inputComponent:
            !isNativeNumber && numberFormat !== undefined
              ? (TextFieldNumberFormatCustom as unknown as React.ElementType<InputBaseComponentProps>)
              : inputSlotProps.inputComponent,
          startAdornment: leftUnit ? (
            <InputAdornment position="start" sx={inputAdornmentSx}>
              {leftUnit}
            </InputAdornment>
          ) : (
            inputSlotProps.startAdornment
          ),
          endAdornment: rightUnit ? (
            <InputAdornment position="end" sx={inputAdornmentSx}>
              {rightUnit}
            </InputAdornment>
          ) : (
            inputSlotProps.endAdornment
          ),
        },
      }}
    />
  );
}
